<template>
   <div class="entry" :class="{ 'no-click': tileType === 'single' }" @click="handleClick">
      <div class="entry-body">
         <!-- Heading Image -->
         <div class="heading-image" :style="headingImageStyle"></div>

         <!-- Heading Section -->
         <div class="heading">
            <div class="title" :class="titleClampClass">
               {{ entry.entry.content.title }}
            </div>
            <div class="stats">
               {{ likesCount }}
            </div>
            <!-- Channel Name clamped to two lines -->
            <div class="meta clamp-two-lines">
               {{ entry.entry.content.channelTitle }}
            </div>
            <a class="plain-link" :href="composeSourceURL()" @click.stop>
               <div class="source">YouTube</div>
            </a>
         </div>

         <!-- Comments Section (if any) -->
         <div
            class="entry-comments"
            v-if="shouldShowComments"
            :style="{ minHeight: commentsMinHeight }"
         >
            <Comment
               v-for="(c, idx) in displayedComments"
               :key="idx"
               :comment="c"
            />
         </div>
      </div>

      <!-- Extra Stats pinned to bottom (shown only when no comments are set to display) -->
      <div class="extra-stats" v-if="showExtraStats">
         {{ extraStatsText }}
      </div>
   </div>
</template>

<script>
import Comment from "./Comment.vue";
const AppLib = require("../../../library/applib.js");

export default {
   name: "Entry",
   components: {
      Comment
   },
   props: {
      entry: {
         type: Object,
         required: true
      },
      // Modes: "tileOneLine", "tile" (or "tileTwoLines") or "single"
      // "single" shows the full entry (no hover/click effect)
      tileType: {
         type: String,
         default: "tile"
      },
      // Number of comments to show (ignored in single mode)
      maxComments: {
         type: Number,
         default: 1
      }
   },
   data() {
      return {
         fullCommentsLoaded: false
      };
   },
   computed: {
      // Use fewer characters on mobile, more on desktop
      commentTruncateLength() {
         return window.innerWidth < 768 ? 128 : 300;
      },
      displayedComments() {
         const all = (this.entry.comments || []).map(c => this.truncatedComment(c));
         if (this.tileType === "single") {
            return all;
         }
         return all.slice(0, this.maxComments);
      },
      shouldShowComments() {
         if (this.tileType === "single") {
            return this.displayedComments.length > 0;
         }
         return this.maxComments > 0 && this.displayedComments.length > 0;
      },
      likesCount() {
         const statsArray = this.entry.entry.stats || [];
         const lastStat = statsArray[statsArray.length - 1] || {};
         const views = lastStat.views || 0;
         return AppLib.scaleFormatNumber(views, 1);
      },
      titleClampClass() {
         if (this.tileType === "tileOneLine") {
            return "clamp-one-line";
         } else if (this.tileType === "single") {
            return "";
         } else {
            return "clamp-two-lines";
         }
      },
      commentsMinHeight() {
         if (this.tileType === "single") {
            return "auto";
         }
         const linesPerComment = 4;
         const lineHeightEm = 1.2;
         const extraOffset = 1.5;
         const totalLines = this.maxComments * linesPerComment;
         return (totalLines * lineHeightEm + extraOffset) + "em";
      },
      headingImageStyle() {
         return {
            backgroundImage: `url(${this.bestThumbUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "10rem",
            width: "100%",
            opacity: "0.9"
         };
      },
      bestThumbUrl() {
         const thumbs = this.entry.entry.thumbnails || {};
         if (thumbs.maxres && thumbs.maxres.url) {
            return thumbs.maxres.url;
         }
         if (thumbs.high && thumbs.high.url) {
            return thumbs.high.url;
         }
         return "https://via.placeholder.com/400x200?text=No+Image";
      },
      showExtraStats() {
         return this.maxComments === 0 && this.tileType !== "single";
      },
      extraStatsText() {
         const commentCount = this.entry.comments ? this.entry.comments.length : 0;
         return `Comments: ${commentCount}, Likes: ${this.likesCount}`;
      }
   },
   methods: {
      handleClick() {
         // Only allow click-to-navigate if not in single mode
         if (this.tileType !== "single") {
            this.goToEntry();
         }
      },
      goToEntry() {
         window.location.href = this.entryUrl();
      },
      composeSourceURL() {
         return "https://www.youtube.com/watch?v=" + this.entry.entryId;
      },
      entryUrl() {
         return "/e/" + this.entry.entry.urlId;
      },
      truncatedComment(original) {
         const copy = JSON.parse(JSON.stringify(original));
         if (
            copy.content &&
            copy.content.textDisplay &&
            copy.content.textDisplay.length > this.commentTruncateLength
         ) {
            copy.content.textDisplay = copy.content.textDisplay.slice(0, this.commentTruncateLength) + "...";
         }
         return copy;
      },
      getFullEntry() {
         if (this.fullCommentsLoaded) return;
         // Simulate API call to load full comments
         setTimeout(() => {
            if (!this.entry.comments) {
               this.$set(this.entry, "comments", []);
            }
            this.entry.comments.push({
               content: {
                  textDisplay: "Additional comment loaded from backend.",
                  authorDisplayName: "BackendUser",
                  publishedAt: "2023-01-03"
               },
               stats: [{ likes: 3 }]
            });
            this.fullCommentsLoaded = true;
         }, 500);
      }
   },
   mounted() {
      if (this.tileType === "single") {
         this.getFullEntry();
      }
   }
};
</script>

<style scoped>
.entry {
   display: flex;
   flex-direction: column;
   background: #fff;
   border-radius: 8px;
   overflow: hidden;
   box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
   transition: transform 0.3s ease, box-shadow 0.3s ease;
   cursor: pointer;
}
.entry.no-click {
   cursor: default;
   pointer-events: auto; /* still allow inner links to be clickable */
}
.entry:hover {
   transform: translateY(-2px);
   box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}
.entry.no-click:hover {
   transform: none;
   box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}
.entry-body {
   flex: 1;
   display: flex;
   flex-direction: column;
}
.plain-link {
   text-decoration: none;
   color: inherit;
}
.heading-image {
   /* Background image is set inline via headingImageStyle */
}
.heading {
   display: grid;
   grid-template-areas:
      "title stats"
      "meta source";
   grid-template-columns: 1fr auto;
   grid-gap: 0.3rem;
   padding: 0.5rem 0.8rem;
}
.title {
   grid-area: title;
   font-size: 1rem;
   font-weight: bold;
   color: #333;
}
.clamp-one-line {
   display: -webkit-box;
   -webkit-line-clamp: 1;
   -webkit-box-orient: vertical;
   overflow: hidden;
   text-overflow: ellipsis;
}
.clamp-two-lines {
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
   overflow: hidden;
   text-overflow: ellipsis;
}
.stats {
   grid-area: stats;
   font-size: 0.9rem;
   color: #2c7;
   text-align: right;
}
.meta {
   grid-area: meta;
   font-size: 0.85rem;
   color: #666;
   overflow: hidden;
   text-overflow: ellipsis;
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
}
.source {
   grid-area: source;
   font-size: 0.8rem;
   color: #999;
   text-align: right;
}
.entry-comments {
   border-top: 1px solid #eee;
   padding: 0.5rem 0.8rem;
}
.extra-stats {
   margin-top: auto;
   background: #f9f9f9;
   padding: 0.5rem 0.8rem;
   font-size: 0.85rem;
   color: #555;
   text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/site/Entry.vue